import React, { useState, useEffect } from 'react';
import { Alert, alpha, useTheme } from '@mui/material';

/**
 * PageAlert - Standardized premium alert for the application.
 * Highlights:
 * - Automatically handles dismissal state.
 * - Re-appears if the error message changes.
 * - Premium styling with backdrop-blur and soft colors.
 */
const PageAlert = ({ error, severity = 'error', sx = {}, ...props }) => {
    const theme = useTheme();
    const [isVisible, setIsVisible] = useState(false);
    const [lastError, setLastError] = useState(null);

    // Show whenever a new error message arrives
    useEffect(() => {
        if (error && error !== lastError) {
            setIsVisible(true);
            setLastError(error);
        }
    }, [error, lastError]);

    if (!error || !isVisible) return null;

    const getBaseColor = () => {
        if (severity === 'error') return '#ef4444';
        if (severity === 'warning') return '#f59e0b';
        if (severity === 'success') return '#10b981';
        return theme.palette.primary.main;
    };

    const baseColor = getBaseColor();

    return (
        <Alert
            severity={severity}
            onClose={() => setIsVisible(false)}
            sx={{
                mb: 4,
                borderRadius: 4,
                fontWeight: 600,
                bgcolor: alpha(baseColor, 0.1),
                color: baseColor,
                border: `1px solid ${alpha(baseColor, 0.2)}`,
                backdropFilter: 'blur(10px)',
                '& .MuiAlert-icon': { color: baseColor },
                '& .MuiAlert-action .MuiIconButton-root': { color: baseColor },
                ...sx
            }}
            {...props}
        >
            {typeof error === 'string' ? error : (error?.message || (error ? String(error) : 'An unexpected error occurred'))}
        </Alert>
    );
};

export default PageAlert;
